import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { Button } from './Button.js';
import { IconButton } from './IconButton.js';
import { cx } from '../utils/cx.js';
import { useOverlayFocusTrap } from '../utils/useOverlayFocusTrap.js';
import { isTopOverlay, popOverlay, pushOverlay } from '../utils/overlayEscStack.js';
import { overlayPresenceProps } from '../hooks/mistRail.js';
import styles from './Modal.module.css';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export type ModalProps = {
  opened: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  /** Extra class on the modal panel. */
  className?: string;
  /** Extra class on the modal body. */
  bodyClassName?: string;
};

export function Modal({ opened, onClose, title, children, footer, size = 'sm', className, bodyClassName }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useOverlayFocusTrap(opened, panelRef);

  useEffect(() => {
    if (!opened) return;
    const stackId = pushOverlay();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Nested overlays (drawers/modals above): only the top-most closes.
      if (!isTopOverlay(stackId)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      popOverlay(stackId);
    };
  }, [opened, onClose]);

  if (!opened) return null;

  const node = (
    <div className={styles.backdrop} role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={panelRef}
        className={cx(styles.panel, styles[size], className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...overlayPresenceProps(opened)}
      >
        <div className={styles.header}>
          <span id={titleId} className={styles.title}>
            {title}
          </span>
          <IconButton
            type="button"
            variant="ghost"
            size="md"
            className={styles.close}
            aria-label="Fermer"
            onClick={onClose}
          >
            <IconX size={22} stroke={1.5} aria-hidden />
          </IconButton>
        </div>
        <div className={cx(styles.body, bodyClassName)}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

export type ConfirmModalFooterProps = {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

export function ConfirmModalFooter({ onCancel, onConfirm, confirmLabel = 'Confirmer', cancelLabel = 'Annuler', danger }: ConfirmModalFooterProps) {
  return (
    <>
      <Button variant="ghost" onClick={onCancel}>
        {cancelLabel}
      </Button>
      <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>
        {confirmLabel}
      </Button>
    </>
  );
}
